import React, { useCallback, useMemo, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  NodeTypes,
  Node,
  Edge,
  OnNodesChange,
  OnEdgesChange,
  applyNodeChanges,
  applyEdgeChanges,
  ConnectionLineType,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ZapNode } from './ZapNode';
import { useStore } from '../store/useStore';
import { FlowNode } from '../types/zapier';

const nodeTypes: NodeTypes = {
  zapNode: ZapNode,
};

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: false,
  style: { strokeWidth: 2 },
};

// Inner component that has access to useReactFlow
const FlowGraphInner: React.FC = () => {
  const { getCurrentWorkflow, setSelectedNode, hoveredVariableSource, hoveredVariableConnection } = useStore();
  const workflow = getCurrentWorkflow();
  const { fitView } = useReactFlow();

  const [baseNodes, setBaseNodes] = React.useState<Node[]>([]);
  const [baseEdges, setBaseEdges] = React.useState<Edge[]>([]);

  // Update nodes and edges when workflow changes
  React.useEffect(() => {
    if (workflow) {
      setBaseNodes(workflow.nodes as Node[]);
      setBaseEdges(workflow.edges as Edge[]);
    } else {
      setBaseNodes([]);
      setBaseEdges([]);
    }
  }, [workflow]);

  // Listen for PDF export trigger to fit view before screenshot
  useEffect(() => {
    const handleFitView = (event: Event) => {
      const customEvent = event as CustomEvent;
      const duration = customEvent.detail?.duration ?? 0;

      // Call fitView with specified duration (0 for instant)
      fitView({ duration, padding: 0.1 });

      // Notify that fit view is complete
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('pdf-fit-view-complete'));
      }, duration + 50); // Small delay to ensure rendering is complete
    };

    document.addEventListener('pdf-trigger-fit-view', handleFitView);

    return () => {
      document.removeEventListener('pdf-trigger-fit-view', handleFitView);
    };
  }, [fitView]);

  // Apply highlighting based on hovered variable source
  const nodes = useMemo(() => {
    if (!hoveredVariableSource) return baseNodes;

    return baseNodes.map(node => {
      const isHighlighted = node.id === hoveredVariableSource;
      if (!isHighlighted) {
        return {
          ...node,
          style: {
            ...node.style,
            opacity: 0.4,
            transition: 'opacity 0.2s ease-in-out',
          },
        };
      }
      return {
        ...node,
        style: {
          ...node.style,
          opacity: 1,
          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.5), 0 10px 25px rgba(59, 130, 246, 0.3)',
          transition: 'all 0.2s ease-in-out',
        },
        className: node.className ? `${node.className} react-flow__node-highlighted` : 'react-flow__node-highlighted',
      };
    });
  }, [baseNodes, hoveredVariableSource]);

  const edges = useMemo(() => {
    let processedEdges = baseEdges;

    // Highlight existing edges when hovering a variable source
    if (hoveredVariableSource) {
      processedEdges = baseEdges.map(edge => {
        const isConnectedToSource = edge.source === hoveredVariableSource || edge.target === hoveredVariableSource;
        return {
          ...edge,
          style: {
            ...edge.style,
            opacity: isConnectedToSource ? 1 : 0.2,
            strokeWidth: isConnectedToSource ? 3 : 2,
            stroke: isConnectedToSource ? '#3b82f6' : undefined,
          },
          animated: isConnectedToSource,
        };
      });
    }

    // Add temporary connection line when hovering a variable
    if (hoveredVariableConnection) {
      const { sourceNodeId, targetNodeId, dataType } = hoveredVariableConnection;

      // Color mapping for data types
      const dataTypeColors = {
        text: '#3b82f6',      // Blue
        number: '#10b981',    // Green
        array: '#a855f7',     // Purple
        object: '#f59e0b',    // Amber
        boolean: '#ec4899',   // Pink
        unknown: '#6b7280',   // Gray
      };

      const connectionEdge: Edge = {
        id: `variable-connection-${sourceNodeId}-${targetNodeId}`,
        source: sourceNodeId,
        target: targetNodeId,
        type: 'smoothstep',
        animated: true,
        style: {
          stroke: dataTypeColors[dataType],
          strokeWidth: 3,
          strokeDasharray: '5,5',
          opacity: 1,
        },
        label: dataType !== 'unknown' ? dataType : undefined,
        labelStyle: {
          fill: dataTypeColors[dataType],
          fontWeight: 600,
          fontSize: 11,
        },
        labelBgStyle: {
          fill: '#ffffff',
          fillOpacity: 0.9,
        },
        labelBgPadding: [4, 6] as [number, number],
        labelBgBorderRadius: 4,
        className: 'variable-connection-edge',
      };

      processedEdges = [...processedEdges, connectionEdge];
    }

    return processedEdges;
  }, [baseEdges, hoveredVariableSource, hoveredVariableConnection]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => setBaseNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => setBaseEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      // Find the original FlowNode with full data
      const flowNode = workflow?.nodes.find((n) => n.id === node.id);
      if (flowNode) {
        setSelectedNode(flowNode as FlowNode);
      }
    },
    [workflow, setSelectedNode]
  );

  const proOptions = useMemo(() => ({ hideAttribution: true }), []);

  if (!workflow) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>No workflow loaded</p>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        connectionLineType={ConnectionLineType.SmoothStep}
        fitView
        minZoom={0.2}
        maxZoom={2}
        proOptions={proOptions}
        className="bg-background"
      >
        <Background className="bg-muted/30" gap={16} />
        <Controls className="bg-card border border-border rounded-lg shadow-lg" />
        <MiniMap
          className="bg-card border border-border rounded-lg shadow-lg"
          nodeColor={(node) => {
            const data = node.data as FlowNode['data'];
            switch (data.nodeType) {
              case 'trigger':
                return '#10b981';
              case 'action':
                return '#3b82f6';
              case 'filter':
                return '#f59e0b';
              case 'code':
                return '#a855f7';
              case 'branch':
                return '#ec4899';
              default:
                return '#6b7280';
            }
          }}
        />
      </ReactFlow>
    </div>
  );
};

// Wrapper component with ReactFlowProvider
export const FlowGraph: React.FC = () => {
  return (
    <ReactFlowProvider>
      <FlowGraphInner />
    </ReactFlowProvider>
  );
};
